import React, { useState } from 'react';
import { Grid, TextField, Typography, Box, Button, Card, CardContent, useTheme } from '@mui/material';

import { generateTenantToken, makeEncryptedApiCall } from "./utils/apiUtils";

function BankCASAForm({ setScore, onComputed, setErrorMessage, setOpenSnackbar, setIsLoading, setFixedPay, setVariablePay }) {
  const theme = useTheme();
  const [tenant, setTenant] = useState({ tenantId: '1', tenantName: '' });
  const [form, setForm] = useState({
    initialDeposit: '50,000',
    income: '5,00,000',
    occupation: 'Salaried',
    age: '30',
    relationships: '2',
    location: 'Mumbai'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTenant = (e) => setTenant({ ...tenant, [e.target.name]: e.target.value });
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const params = [
    { name: 'Initial Deposit Value', score: 70, weight: 0.18 },
    { name: 'Predicted Average Balance (PAB) Score', score: 85, weight: 0.28 },
    { name: 'Transaction Velocity Index', score: 20, weight: 0.16 },
    { name: 'Digital Adoption Propensity', score: 95, weight: 0.10 },
    { name: 'Cross-sell Readiness Score', score: 58, weight: 0.14 },
    { name: 'Account Longevity Probability', score: 90, weight: 0.11 },
    { name: 'Referral Network Potential', score: 15, weight: 0.07 },
    { name: 'Geographic Penetration Value', score: 40, weight: 0.06 }
  ];

  const handleSubmit = async () => {
    // Validate tenant information
    if (!tenant.tenantId.trim() || !tenant.tenantName.trim()) {
      setErrorMessage("Please enter both Tenant ID and Tenant Name.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }
    // setIsSubmitting(true);
    // setTimeout(() => {
      // const scores = params.reduce((obj, p) => { obj[p.name.replace(/\s+/g, '_').toLowerCase()] = p.score; return obj; }, {});
      try {
        // Step 1: Generate tenant token
        const tokenResponse = await generateTenantToken(tenant.tenantId, tenant.tenantName);
        
        // Step 2: Make encrypted API call
        const url = `/api/v1.0/tenants/${tenant.tenantId}/score/bank/casa`;
        const score = await makeEncryptedApiCall(url, form, tokenResponse.token);
        
        setScore(score.output);
        const weights = params.reduce((obj, p) => { obj[p.name.replace(/\s+/g, '_').toLowerCase()] = p.weight; return obj; }, {});
      // setScore(scores);
        onComputed && onComputed({ weights, finalScore: 88, finalPay: 938.5, formula });
        setFixedPay(500);
        setVariablePay(200);
        // setIsSubmitting(false);
      } catch (error) {
        console.error("Error fetching bank score:", error);
        let detailedMessage = "An error occurred while fetching the bank score.";
        if (error.response && error.response.data && error.response.data.error) {
          detailedMessage = error.response.data.error;
        } else if (error.message) {
          detailedMessage = error.message;
        }
        setErrorMessage(detailedMessage);
        setOpenSnackbar(true);
      } finally {
        setIsSubmitting(false);
        setIsLoading(false);
      }
    // }, 3000);
  };

  const formula = '(70*0.18) + (85*0.28) + (80*0.16) + (95*0.10) + (78*0.14) + (90*0.11) + (65*0.07) + (60*0.06) = 87.67';

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Tenant Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField name="tenantId" value={tenant.tenantId} onChange={handleTenant} fullWidth size="small" placeholder="Tenant ID" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="tenantName" value={tenant.tenantName} onChange={handleTenant} fullWidth size="small" placeholder="Tenant Name" autoComplete="off" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Input fields (not used in hardcoded calc but shown per spec) */}
      {[
        { name: 'initialDeposit', label: 'Initial Deposit' },
        { name: 'income', label: 'Customer Income' },
        { name: 'occupation', label: 'Occupation' },
        { name: 'age', label: 'Customer Age' },
        { name: 'relationships', label: 'Existing Banking Relationships' },
        { name: 'location', label: 'Location' }
      ].map((f) => (
        <Grid item xs={12} sm={6} key={f.name}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={500}>{f.label}</Typography>
          </Box>
          <TextField name={f.name} value={form[f.name]} onChange={handleChange} fullWidth size="small" placeholder={`Enter ${f.label}`} />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button 
        variant="contained" 
        fullWidth 
        size="large"
        onClick={handleSubmit} 
        disabled={isSubmitting} 
        sx={{
          mt: 2, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1rem', fontWeight: 600,
          background: 'linear-gradient(135deg,rgb(88, 139, 249) 0%, #2563EB 100%)',
          boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
          '&:hover': { boxShadow: '0 15px 30px rgba(37, 99, 235, 0.3)', transform: 'translateY(-2px)' },
          transition: 'all 0.3s ease',
          '&.Mui-disabled': {
              background: theme.palette.grey[300],
              boxShadow: 'none',
              color: theme.palette.grey[500],
              cursor: 'not-allowed'
          }
        }}

        >
          {isSubmitting ? 'Calculating...' : 'Calculate CASA Score'}
        </Button>
      </Grid>
    </Grid>
  );
}

export default BankCASAForm;


